/**
 * Persisted panel width state.
 *
 * Reads once from localStorage on mount, exposes a setter that debounces
 * writes to avoid spamming storage during a drag. Always clamps to
 * [min, max] — protects against stale stored values when we later tighten
 * the bounds.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { STORAGE_KEY_PREFIX, clamp } from './constants';

const read = (key: string, fallback: number, min: number, max: number): number => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return clamp(parsed, min, max);
  } catch {
    return fallback;
  }
};

export type UsePanelWidthOptions = {
  /** Stable identifier used as the localStorage key suffix. */
  panelId: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** When false, width is not persisted (useful for tests). */
  persist?: boolean;
};

export const usePanelWidth = ({
  panelId,
  defaultWidth,
  minWidth,
  maxWidth,
  persist = true,
}: UsePanelWidthOptions) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${panelId}`;

  // Lazy initializer so we only hit localStorage once.
  const [width, setWidthState] = useState<number>(() =>
    persist ? read(storageKey, defaultWidth, minWidth, maxWidth) : defaultWidth,
  );

  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setWidth = useCallback(
    (next: number) => {
      const clamped = clamp(next, minWidth, maxWidth);
      setWidthState(clamped);
      if (!persist) return;
      if (writeTimer.current) clearTimeout(writeTimer.current);
      writeTimer.current = setTimeout(() => {
        try {
          localStorage.setItem(storageKey, String(clamped));
        } catch {
          // ignore — storage full / disabled
        }
      }, 120);
    },
    [maxWidth, minWidth, persist, storageKey],
  );

  useEffect(
    () => () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    },
    [],
  );

  const resetWidth = useCallback(() => setWidth(defaultWidth), [defaultWidth, setWidth]);

  return { width, setWidth, resetWidth };
};
